import React, {
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useState
} from 'react';
import { isEqual } from 'lodash';
import BulkActionWizardView from '../view/BulkActionWizardView';
import { FormBuilderContext } from './FormBuilderEngine';
import generateId from '../../utils/generateId';
import { BulkActionsContext } from './FormBuilderBulkActionsEngine';
import useTranslations from '../../hooks/useTranslations';

const BulkActionWizardReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};
const initialData = {
  id: undefined,
  label: '',
  confirmationText: 'Please review and confirm the following actions:',
  action: '',
  statuses: [],
  updateValue: ''
};

const BulkActionWizardEngine = ({
  actionData = initialData,
  save = actionData => actionData
}) => {
  const t = useTranslations();
  const [data, dispatch] = useReducer(BulkActionWizardReducer, actionData);
  const { form } = useContext(FormBuilderContext);
  const { onWizardClosed } = useContext(BulkActionsContext);

  const [isNoStatusesDialogOpen, setIsNoStatusesDialogOpen] = useState(false);
  const [isWizardDirtyDialogOpen, setIsWizardDirtyDialogOpen] = useState(false);
  const [confirmationEditMode, setConfirmationEditMode] = useState(false);
  const [confirmationTextWas, setConfirmationTextWas] = useState('');

  const reasonsSaveIsDisabled = useMemo(() => {
    let reasons = [];

    if (!(data.label || '').trim()) {
      reasons.push(`${t('bulkAction.wizard.error.missingLabel')}`);
    }
    if (!(data.confirmationText || '').trim()) {
      reasons.push(`${t('bulkAction.wizard.error.missingConfirmationText')}`);
    }
    if (!(data.statuses || []).length) {
      reasons.push(`${t('bulkAction.wizard.error.missingStatuses')}`);
    }
    if (!(data.updateValue || []).length) {
      reasons.push(`${t('bulkAction.wizard.error.missingValue')}`);
    }
    return reasons.length
      ? t('bulkAction.wizard.error.sentence', {
          reasons: reasons.join(', ')
        })
      : '';
  }, [data]);

  const onBackClicked = useCallback(() => {
    if (!isEqual(data, actionData)) {
      setIsWizardDirtyDialogOpen(true);
      return;
    }

    onWizardClosed();
  }, [data]);

  const onSave = useCallback(
    forceSave => {
      if (!data.statuses.length && !forceSave) {
        setIsNoStatusesDialogOpen(true);
        return;
      }

      const id = data.id || generateId();
      save({ ...data, id });
      onWizardClosed();
    },
    [data, save]
  );

  const onStatusDDChange = useCallback(
    values => {
      const isAllSelected = data.statuses.includes('all');
      const formStatuses = form.customStatuses || [];
      let newValues = [];

      if (values.includes('all') && !isAllSelected) {
        newValues = ['all', ...formStatuses.map(s => s.internalId)];
      } else if (values.includes('all') && isAllSelected) {
        newValues = values.filter(s => s !== 'all');
      } else {
        newValues = values;
      }

      dispatch({ type: 'update', payload: { statuses: newValues } });
    },
    [data, form]
  );

  const onClickChangeConfirmationText = useCallback(() => {
    setConfirmationEditMode(true);
    setConfirmationTextWas(data.confirmationText);
  }, [data]);

  const onClickSaveConfirmationText = useCallback(() => {
    setConfirmationEditMode(false);
    setConfirmationTextWas('');
  }, []);

  const onClickCancelConfirmationText = useCallback(() => {
    dispatch({
      type: 'update',
      payload: { confirmationText: confirmationTextWas }
    });
    setConfirmationEditMode(false);
    setConfirmationTextWas('');
  }, [confirmationTextWas]);

  const statusesValues = useMemo(() => {
    if (data.statuses && data.statuses.includes('all')) {
      return ['all', ...(form.customStatuses || []).map(cs => cs.internalId)];
    }
    return data.statuses;
  }, [data, form]);

  const viewProps = {
    ...data,
    confirmationEditMode,
    isNoStatusesDialogOpen,
    setIsNoStatusesDialogOpen,
    dispatch,
    onBackClicked,
    onClickChangeConfirmationText,
    onClickSaveConfirmationText,
    onClickCancelConfirmationText,
    confirmationTextWas,
    onSave,
    onStatusDDChange,
    onWizardClosed,
    reasonsSaveIsDisabled,
    statuses: statusesValues
  };

  return <BulkActionWizardView {...viewProps} />;
};

export default BulkActionWizardEngine;
